import React, { useEffect, useState } from 'react';
import { useAffiliate } from '@/contexts/AffiliateContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL as API, BACKEND_URL } from '@/utils/apiConfig';
import { toast } from 'sonner';
import {
  KeyRound, LogOut, Users, DollarSign, TrendingUp, Copy, Check,
  ExternalLink, Clock, ArrowUpRight, Percent, CalendarDays,
  UserPlus, CreditCard, FileText, FileSpreadsheet, Download,
  Link2, ShieldCheck, ShieldOff, Banknote, AlertTriangle, CheckCircle2, XCircle, Info
} from 'lucide-react';

export default function AffiliateDashboardPage() {
  const { affiliate, logout, checkAuth } = useAffiliate();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedVanity, setCopiedVanity] = useState(false);

  // Custom slug state
  const [editSlug, setEditSlug] = useState(false);
  const [slugInput, setSlugInput] = useState('');
  const [slugSaving, setSlugSaving] = useState(false);

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [disablePw, setDisablePw] = useState('');

  // Payout state
  const [payoutData, setPayoutData] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [requestingPayout, setRequestingPayout] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [dashRes, eligRes, histRes] = await Promise.all([
          axios.get(`${API}/affiliate/dashboard`, { withCredentials: true }),
          axios.get(`${API}/affiliate/payout-eligibility`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/affiliate/payout-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setData(dashRes.data);
        setPayoutData(eligRes.data);
        setPayoutHistory(histRes.data);
      } catch (err) {
        console.error('Failed to fetch dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleLogout = async () => { await logout(); navigate('/affiliate'); };

  const affInfo = data?.affiliate || affiliate || {};
  const referralLink = affInfo.ref_code ? `${window.location.origin}/?ref=${affInfo.ref_code}` : '';
  const vanityLink = affInfo.custom_slug ? `${BACKEND_URL}/api/r/${affInfo.custom_slug}` : '';

  const copyLink = (text, setter) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const handleSlugSave = async () => {
    setSlugSaving(true);
    try {
      await axios.put(`${API}/affiliate/custom-slug`, { slug: slugInput }, { withCredentials: true });
      toast.success(slugInput ? 'Custom link saved!' : 'Custom link removed');
      await checkAuth();
      setEditSlug(false);
      // Refresh dashboard data
      const res = await axios.get(`${API}/affiliate/dashboard`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSlugSaving(false);
    }
  };

  const handle2FASetup = async () => {
    try {
      const res = await axios.post(`${API}/affiliate/2fa/setup`, {}, { withCredentials: true });
      setTwoFASetup(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to setup 2FA');
    }
  };

  const handle2FAVerify = async () => {
    try {
      await axios.post(`${API}/affiliate/2fa/verify`, { code: twoFACode }, { withCredentials: true });
      toast.success('2FA enabled successfully!');
      setTwoFASetup(null);
      setTwoFACode('');
      setShow2FA(false);
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    }
  };

  const handle2FADisable = async () => {
    try {
      await axios.post(`${API}/affiliate/2fa/disable`, { password: disablePw }, { withCredentials: true });
      toast.success('2FA disabled');
      setDisablePw('');
      setShow2FA(false);
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable 2FA');
    }
  };

  const handleRequestPayout = async () => {
    setRequestingPayout(true);
    try {
      await axios.post(`${API}/affiliate/payout-request`, {}, { withCredentials: true });
      toast.success('Payout request submitted!');
      const [eligRes, histRes] = await Promise.all([
        axios.get(`${API}/affiliate/payout-eligibility`, { withCredentials: true }),
        axios.get(`${API}/affiliate/payout-requests`, { withCredentials: true }),
      ]);
      setPayoutData(eligRes.data);
      setPayoutHistory(histRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ background: '#0f1729' }}>
        <div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const recentReferrals = data?.recent_referrals || [];
  const recentCommissions = data?.recent_commissions || [];
  const monthlySignups = data?.monthly_signups || [];
  const payoutRules = payoutData?.rules || {};
  const commissionTypeLabel = {
    one_time: 'One-time', time_limited: `${affInfo.commission_duration_months || 0} months`, perpetual: 'Perpetual (lifetime)',
  };

  return (
    <div className="page-container" style={{ background: '#0f1729' }}>
      {/* Header */}
      <header className="border-b border-white/10 px-4 sm:px-6 py-4 shrink-0">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <KeyRound className="h-4 w-4 text-indigo-400" />
            </div>
            <span className="font-bold text-white text-sm">Eili Affiliate</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 hidden sm:block">{affInfo.email}</span>
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400 transition px-3 py-1.5 rounded-lg hover:bg-white/5"
              data-testid="affiliate-logout-btn">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="page-content max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-24 space-y-6 w-full">
        {/* Welcome + Referral Links */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h1 className="text-lg font-semibold text-white mb-1" data-testid="affiliate-welcome">Welcome, {affInfo.name}</h1>
          <p className="text-sm text-slate-400 mb-4">Share your referral link to earn commissions on paid subscriptions.</p>

          {/* Standard referral link */}
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <div className="flex-1 bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 flex items-center gap-2 min-w-0">
              <ExternalLink className="h-4 w-4 text-indigo-400 shrink-0" />
              <span className="text-sm text-slate-300 truncate font-mono" data-testid="affiliate-ref-link">{referralLink}</span>
            </div>
            <button onClick={() => copyLink(referralLink, setCopied)}
              className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition shrink-0"
              data-testid="affiliate-copy-link-btn">
              {copied ? <><Check className="h-4 w-4" /> Copied!</> : <><Copy className="h-4 w-4" /> Copy Link</>}
            </button>
          </div>

          {/* Custom vanity link */}
          {vanityLink && !editSlug && (
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <div className="flex-1 bg-black/30 border border-indigo-500/30 rounded-xl px-4 py-2.5 flex items-center gap-2 min-w-0">
                <Link2 className="h-4 w-4 text-indigo-400 shrink-0" />
                <span className="text-sm text-indigo-300 truncate font-mono" data-testid="affiliate-vanity-link">{vanityLink}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => copyLink(vanityLink, setCopiedVanity)}
                  className="flex items-center justify-center gap-2 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 text-sm font-medium px-4 py-2.5 rounded-xl transition border border-indigo-500/20 shrink-0">
                  {copiedVanity ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
                <button onClick={() => { setEditSlug(true); setSlugInput(affInfo.custom_slug || ''); }}
                  className="text-xs text-slate-400 hover:text-indigo-400 transition px-3 py-2 rounded-xl hover:bg-white/5">Edit</button>
              </div>
            </div>
          )}

          {/* Edit/Create custom slug */}
          {(editSlug || (!vanityLink && !editSlug)) && (
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <div className="flex-1 flex items-center bg-black/30 border border-white/10 rounded-xl overflow-hidden">
                <span className="text-xs text-slate-500 pl-4 shrink-0">{BACKEND_URL}/api/r/</span>
                <input value={slugInput} onChange={e => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className="flex-1 bg-transparent text-sm text-white py-2.5 pr-4 focus:outline-none font-mono"
                  placeholder="your-custom-slug" data-testid="affiliate-slug-input" />
              </div>
              <div className="flex gap-2">
                <button onClick={handleSlugSave} disabled={slugSaving}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition disabled:opacity-50 shrink-0"
                  data-testid="affiliate-slug-save-btn">
                  {slugSaving ? '...' : 'Save'}
                </button>
                {vanityLink && (
                  <button onClick={() => setEditSlug(false)}
                    className="text-xs text-slate-400 hover:text-white transition px-3 py-2 rounded-xl hover:bg-white/5">Cancel</button>
                )}
              </div>
            </div>
          )}
          {!vanityLink && !editSlug && (
            <button onClick={() => setEditSlug(true)}
              className="flex items-center gap-2 text-xs text-indigo-400 hover:text-indigo-300 transition mb-3"
              data-testid="affiliate-create-slug-btn">
              <Link2 className="h-3.5 w-3.5" /> Set up a custom vanity link
            </button>
          )}

          {/* Commission Info */}
          <div className="flex flex-wrap gap-3 mt-2">
            <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-white/5 px-3 py-1.5 rounded-lg">
              <Percent className="h-3 w-3 text-indigo-400" /><span>{affInfo.commission_rate}% commission</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-white/5 px-3 py-1.5 rounded-lg">
              <Clock className="h-3 w-3 text-indigo-400" /><span>{commissionTypeLabel[affInfo.commission_type] || affInfo.commission_type}</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={UserPlus} label="Total Signups" value={stats.total_signups || 0} color="text-blue-400" bgColor="bg-blue-400/10" />
          <StatCard icon={CreditCard} label="Paid Conversions" value={stats.total_conversions || 0} color="text-emerald-400" bgColor="bg-emerald-400/10" />
          <StatCard icon={TrendingUp} label="Conversion Rate" value={`${stats.conversion_rate || 0}%`} color="text-amber-400" bgColor="bg-amber-400/10" />
          <StatCard icon={DollarSign} label="Total Earnings" value={`$${(stats.total_earnings || 0).toFixed(2)}`} color="text-indigo-400" bgColor="bg-indigo-400/10" />
        </div>

        {/* Earnings Breakdown */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1"><Clock className="h-4 w-4 text-amber-400" /><span className="text-sm font-medium text-white">Pending Earnings</span></div>
            <p className="text-2xl font-bold text-amber-400" data-testid="affiliate-pending-earnings">${(stats.pending_earnings || 0).toFixed(2)}</p>
            <p className="text-xs text-slate-500 mt-1">Awaiting payout</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1"><DollarSign className="h-4 w-4 text-emerald-400" /><span className="text-sm font-medium text-white">Paid Out</span></div>
            <p className="text-2xl font-bold text-emerald-400" data-testid="affiliate-paid-earnings">${(stats.paid_earnings || 0).toFixed(2)}</p>
            <p className="text-xs text-slate-500 mt-1">Total received</p>
          </div>
        </div>

        {/* Payout Request Section */}
        {payoutRules.payout_enabled && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="affiliate-payout-section">
            <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <Banknote className="h-4 w-4 text-indigo-400" /> Request Payout
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="bg-black/20 rounded-xl px-4 py-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Min Subscriber Days</p>
                <p className="text-sm font-medium text-white">{payoutRules.min_subscriber_days} days</p>
              </div>
              <div className="bg-black/20 rounded-xl px-4 py-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Min Payout Amount</p>
                <p className="text-sm font-medium text-white">${payoutRules.min_payout_amount?.toFixed(2)}</p>
              </div>
              <div className="bg-black/20 rounded-xl px-4 py-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Eligible Amount</p>
                <p className="text-sm font-medium text-indigo-400">${(payoutData?.eligible_amount || 0).toFixed(2)}</p>
              </div>
            </div>

            {payoutData?.reason && !payoutData?.eligible && (
              <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs rounded-xl px-4 py-3 mb-4">
                <Info className="h-3.5 w-3.5 shrink-0" /> {payoutData.reason}
              </div>
            )}

            <button onClick={handleRequestPayout} disabled={!payoutData?.eligible || requestingPayout}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:hover:bg-indigo-600 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition"
              data-testid="affiliate-request-payout-btn">
              <Banknote className="h-4 w-4" /> {requestingPayout ? 'Submitting...' : 'Request Payout'}
            </button>

            {/* Payout History */}
            {payoutHistory.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-slate-400 mb-2">Payout History</p>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {payoutHistory.map(pr => (
                    <div key={pr.request_id} className="flex items-center justify-between bg-black/20 rounded-xl px-4 py-2.5">
                      <div>
                        <span className="text-sm text-white font-medium">${pr.amount?.toFixed(2)}</span>
                        {pr.admin_notes && <span className="text-xs text-slate-500 ml-2">"{pr.admin_notes}"</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-lg ${
                          pr.status === 'approved' ? 'text-emerald-400 bg-emerald-400/10' :
                          pr.status === 'rejected' ? 'text-red-400 bg-red-400/10' :
                          'text-amber-400 bg-amber-400/10'
                        }`}>
                          {pr.status === 'approved' ? <CheckCircle2 className="h-3 w-3 inline mr-1" /> :
                           pr.status === 'rejected' ? <XCircle className="h-3 w-3 inline mr-1" /> : null}
                          {pr.status}
                        </span>
                        <span className="text-[10px] text-slate-500">{new Date(pr.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 2FA Settings */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="affiliate-2fa-section">
          <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-indigo-400" /> Two-Factor Authentication
          </h3>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-white">{affInfo.totp_enabled ? 'Enabled' : 'Disabled'}</p>
              <p className="text-xs text-slate-500">{affInfo.totp_enabled ? 'Your account is protected with 2FA' : 'Add an extra layer of security'}</p>
            </div>
            <button onClick={() => { setShow2FA(p => !p); if (!affInfo.totp_enabled && !twoFASetup) handle2FASetup(); }}
              className={`flex items-center gap-2 text-xs px-4 py-2 rounded-xl transition border ${
                affInfo.totp_enabled
                  ? 'border-red-500/20 text-red-400 hover:bg-red-500/10'
                  : 'border-indigo-500/20 text-indigo-400 hover:bg-indigo-500/10'
              }`}
              data-testid="affiliate-2fa-toggle-btn">
              {affInfo.totp_enabled ? <><ShieldOff className="h-3.5 w-3.5" /> Disable</> : <><ShieldCheck className="h-3.5 w-3.5" /> Enable</>}
            </button>
          </div>

          {show2FA && !affInfo.totp_enabled && twoFASetup && (
            <div className="space-y-4 bg-black/20 rounded-xl p-4">
              <p className="text-xs text-slate-400">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):</p>
              <div className="flex justify-center">
                <img src={twoFASetup.qr_code} alt="2FA QR Code" className="rounded-lg" style={{ width: 200, height: 200 }} data-testid="affiliate-2fa-qr" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Manual key</p>
                <p className="text-xs font-mono text-slate-300 bg-black/30 px-3 py-2 rounded-lg break-all">{twoFASetup.secret}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Backup codes (save these!)</p>
                <div className="grid grid-cols-4 gap-1">
                  {twoFASetup.backup_codes?.map((c, i) => (
                    <span key={i} className="text-xs font-mono text-slate-300 bg-black/30 px-2 py-1 rounded text-center">{c}</span>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <input value={twoFACode} onChange={e => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm text-center tracking-[0.2em] font-mono placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                  placeholder="Enter 6-digit code" data-testid="affiliate-2fa-verify-input" />
                <button onClick={handle2FAVerify} disabled={twoFACode.length < 6}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition disabled:opacity-50"
                  data-testid="affiliate-2fa-verify-btn">
                  Verify & Enable
                </button>
              </div>
            </div>
          )}

          {show2FA && affInfo.totp_enabled && (
            <div className="space-y-3 bg-black/20 rounded-xl p-4">
              <p className="text-xs text-slate-400">Enter your password to disable 2FA:</p>
              <div className="flex gap-2">
                <input type="password" value={disablePw} onChange={e => setDisablePw(e.target.value)}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
                  placeholder="Your password" data-testid="affiliate-2fa-disable-pw" />
                <button onClick={handle2FADisable}
                  className="bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition"
                  data-testid="affiliate-2fa-disable-btn">
                  Confirm Disable
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Download Reports */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Download className="h-4 w-4 text-indigo-400" /> Download Reports</h3>
          <p className="text-xs text-slate-400 mb-4">Export your performance data for accounting or record-keeping.</p>
          <div className="flex flex-wrap gap-3">
            <a href={`${API}/affiliate/report/pdf`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 text-sm font-medium px-4 py-2.5 rounded-xl transition border border-indigo-500/20"
              data-testid="affiliate-download-pdf"><FileText className="h-4 w-4" /> Download PDF Report</a>
            <a href={`${API}/affiliate/report/csv`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-slate-300 text-sm font-medium px-4 py-2.5 rounded-xl transition border border-white/10"
              data-testid="affiliate-download-csv"><FileSpreadsheet className="h-4 w-4" /> Download CSV Export</a>
          </div>
        </div>

        {/* Monthly Signups */}
        {monthlySignups.length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-indigo-400" /> Monthly Signups</h3>
            <div className="flex items-end gap-2 h-32">
              {monthlySignups.map((m) => {
                const maxCount = Math.max(...monthlySignups.map(x => x.count), 1);
                const height = Math.max((m.count / maxCount) * 100, 4);
                return (
                  <div key={m._id} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-slate-400">{m.count}</span>
                    <div className="w-full rounded-t-lg bg-indigo-500/30 transition-all" style={{ height: `${height}%` }} />
                    <span className="text-[10px] text-slate-500">{m._id?.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Referrals */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-blue-400" /> Recent Referrals</h3>
          {recentReferrals.length === 0 ? (
            <p className="text-sm text-slate-500">No referrals yet. Share your link to get started!</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recentReferrals.map((r) => (
                <div key={r.referral_id} className="flex items-center justify-between bg-black/20 rounded-xl px-4 py-2.5">
                  <div>
                    <p className="text-sm text-white">{r.user_name || 'User'}</p>
                    <p className="text-xs text-slate-500">{r.user_email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.converted ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-lg"><ArrowUpRight className="h-3 w-3" /> Converted</span>
                    ) : (
                      <span className="text-xs text-slate-500 bg-white/5 px-2 py-1 rounded-lg">Free</span>
                    )}
                    <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Commissions */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2"><DollarSign className="h-4 w-4 text-emerald-400" /> Recent Commissions</h3>
          {recentCommissions.length === 0 ? (
            <p className="text-sm text-slate-500">No commissions yet. Commissions are earned when referred users subscribe to a paid plan.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recentCommissions.map((c) => (
                <div key={c.commission_id} className="flex items-center justify-between bg-black/20 rounded-xl px-4 py-2.5">
                  <div>
                    <p className="text-sm text-white">${c.amount.toFixed(2)} <span className="text-slate-500">({c.commission_rate}%)</span></p>
                    <p className="text-xs text-slate-500">Plan: {c.plan} &middot; Payment: ${(c.payment_amount_cents / 100).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-lg ${c.status === 'paid' ? 'text-emerald-400 bg-emerald-400/10' : 'text-amber-400 bg-amber-400/10'}`}>
                      {c.status === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                    <span className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bgColor }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
      <div className={`w-8 h-8 rounded-lg ${bgColor} flex items-center justify-center mb-2`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <p className="text-xl font-bold text-white" data-testid={`affiliate-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}
